import { BreadTransaction } from "./BreadTransaction";
import { FirestoreDocRule } from "./FirestoreDocRule";
import { Predicate } from "./Predicates";
import * as P from "./Predicates";

// We use null in the rule criteria to indicate that a field is not active in
// the rule (i.e that field should be ignored for matching purposes).
// However, some of our field values can be null (i.e. merchant can be null).
// In the case of a null field value, we convert it to a placeholder value (in the case of strings this is the empty string).
export function getNullFieldValueCriterionEquivalent() {
  return "";
}

/**
 * Best INACTIVE Match: this is the rule with the minimum order position that matches the transaction, but where the category does NOT match the one already set on the transaction
 * Best ACTIVE Match: this is the rule with the minimum order position that matches the transaction AND the category already set on that transcation
 *
 * "Best Match":
 *   - for reviewed transcations, return the best active match if one can be found, otherwise fall back to the best inactive match
 *   - for unreviewed transactions, take the best match regardless of whether it is active or not
 *
 * If no match is found, this function returns {
 *     bestMatchRule: null,
 *     bestMatchRuleIsActive: false
 *   }
 */
export function matchTransactionToRule(
  transaction: BreadTransaction,
  rules: Map<string, FirestoreDocRule>,
): {
  bestMatchRule: FirestoreDocRule | null;
  bestMatchRuleIsActive: boolean; // false when bestMatchRule is null or bestMatchRule's category is not the transaction's category
} {
  let bestMatchRule: FirestoreDocRule | null = null;
  let bestMatchRuleIsActive = false;
  for (const [_ruleId, thisRule] of rules) {
    if (transactionMatchesSpecificRule(transaction, thisRule)) {
      const thisRuleIsActive =
        thisRule.action.category_id === transaction.categoryId;
      if (bestMatchRule === null) {
        bestMatchRule = thisRule;
        bestMatchRuleIsActive = thisRuleIsActive;
      } else if (
        !bestMatchRuleIsActive &&
        thisRuleIsActive &&
        transaction.reviewed
      ) {
        bestMatchRule = thisRule;
        bestMatchRuleIsActive = thisRuleIsActive;
      } else if (
        bestMatchRuleIsActive &&
        !thisRuleIsActive &&
        transaction.reviewed
      ) {
        continue;
      } else if (thisRule.order_position < bestMatchRule.order_position) {
        // either thisRuleIsActive must equal bestMatchRuleIsActive here OR the transaction is unreviewed
        bestMatchRule = thisRule;
        bestMatchRuleIsActive = thisRuleIsActive;
      } else {
        continue;
      }
    }
  }
  return { bestMatchRule, bestMatchRuleIsActive };
}

/**
 * What a rules re-run (the web app's "apply rules to unreviewed transactions")
 * should write to one transaction, or null to leave it alone.
 *
 * Bread doesn't record who set a category, so this infers it: a category that
 * a matching rule would set, under the rules as they are now OR as they were
 * just before the change that triggered this re-run, is treated as rule-set
 * and follows the rules (including being cleared when no rule matches any
 * more). Any other category was chosen by the user, or by an AI agent through
 * the MCP connector, and is kept. So editing, deleting or reordering a rule
 * behaves as it always has, while hand-picked categories are never
 * overwritten or cleared.
 *
 *   - reviewed: null. Rules never touch reviewed transactions.
 *   - already what the rules say now: null.
 *   - uncategorized, or rule-set (by a matching rule now or before): the best
 *     current match's category, or no category if nothing matches now.
 *   - anything else: null. The user's (or agent's) choice stands.
 *
 * `rulesBeforeChange` defaults to `rules`, which is right for a plain Sync.
 */
export function categoryUpdateFromRules(
  transaction: BreadTransaction,
  rules: Map<string, FirestoreDocRule>,
  rulesBeforeChange: Map<string, FirestoreDocRule> = rules,
): { category_id: string | null } | null {
  if (transaction.reviewed) {
    return null;
  }
  const matching = (ruleSet: Map<string, FirestoreDocRule>) =>
    Array.from(ruleSet.values()).filter((rule) =>
      transactionMatchesSpecificRule(transaction, rule),
    );
  const matchingNow = matching(rules);
  const best = matchingNow.reduce<FirestoreDocRule | null>(
    (a, b) => (a === null || b.order_position < a.order_position ? b : a),
    null,
  );
  const target = best ? best.action.category_id : null;
  const current = transaction.categoryId;
  if (current === target) {
    return null;
  }
  const setByRule =
    current === null ||
    [...matchingNow, ...matching(rulesBeforeChange)].some(
      (rule) => rule.action.category_id === current,
    );
  return setByRule ? { category_id: target } : null;
}

/**
 * This checks whether a specific rule matches a transaction.
 * You usually want to use the `matchTransactionToRule` function instead.
 * You should ABSOLUTELY NOT loop over this function to find a matching rule.
 */
export function transactionMatchesSpecificRule(
  transaction: BreadTransaction,
  rule: FirestoreDocRule,
): boolean {
  return predicateFromRule(rule).applyTo(transaction);
}

function predicateFromRule(rule: FirestoreDocRule): Predicate {
  const nullPlaceholder = getNullFieldValueCriterionEquivalent();
  const criteria = rule.criteria;
  const predicates: Predicate[] = [];
  if (criteria.account_id === nullPlaceholder) {
    predicates.push(
      P.IsNullOrEmpty(P.Field<BreadTransaction, "account_id">("account_id")),
    );
  } else if (criteria.account_id != null) {
    predicates.push(
      P.Equals(
        P.Field<BreadTransaction, "account_id">("account_id"),
        P.Literal(criteria.account_id),
      ),
    );
  }
  if (criteria.merchant_name === nullPlaceholder) {
    predicates.push(
      P.IsNullOrEmpty(
        P.NestedField<BreadTransaction, "merchant", "name">("merchant", "name"),
      ),
    );
  } else if (criteria.merchant_name != null) {
    predicates.push(
      P.Equals(
        P.NestedField<BreadTransaction, "merchant", "name">("merchant", "name"),
        P.Literal(criteria.merchant_name),
      ),
    );
  }
  if (criteria.description === nullPlaceholder) {
    predicates.push(
      P.IsNullOrEmpty(P.Field<BreadTransaction, "description">("description")),
    );
  } else if (criteria.description != null) {
    predicates.push(
      P.Equals(
        P.Field<BreadTransaction, "description">("description"),
        P.Literal(criteria.description),
      ),
    );
  }
  if (criteria.partial_description != null) {
    predicates.push(
      P.SubstringCI(
        P.Field<BreadTransaction, "description">("description"),
        P.Literal(criteria.partial_description!),
      ),
    );
  }
  if (criteria.type === nullPlaceholder) {
    predicates.push(P.IsNullOrEmpty(P.Field<BreadTransaction, "type">("type")));
  } else if (criteria.type != null) {
    predicates.push(
      P.Equals(
        P.Field<BreadTransaction, "type">("type"),
        P.Literal(criteria.type),
      ),
    );
  }
  if (criteria.subtype === nullPlaceholder) {
    predicates.push(
      P.IsNullOrEmpty(P.Field<BreadTransaction, "subtype">("subtype")),
    );
  } else if (criteria.subtype != null) {
    predicates.push(
      P.Equals(
        P.Field<BreadTransaction, "subtype">("subtype"),
        P.Literal(criteria.subtype),
      ),
    );
  }
  return P.All(...predicates);
}
